import React, { useCallback, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartLine, faChartPie, faCheckCircle, faFilter, faHourglassHalf,
  faSearch, faTicket, faTrophy, faUsers, faXmark,
} from "@fortawesome/free-solid-svg-icons";
import {
  Bar, BarChart, CartesianGrid, Cell, LabelList, Legend, Line, LineChart,
  Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import Breadcrumb from "../../components/Ui_Element/Breadcrumb";
import DotLoader from "../../components/Ui_Element/DotLoader";
import { showToast } from "../../components/Ui_Element/CustomToast";
import {
  fetchCustomerTicketAnalytics,
  getDefaultCustomerTicketFilters,
} from "./customerTicketAnalyticsService";

const BRAND_135 = "linear-gradient(135deg, #C04C2A 0%, #8B2A15 55%, #4A1B13 100%)";

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 6 }, (_, i) => CURRENT_YEAR - i);
const MONTHS = [
  { value: "1", label: "January" }, { value: "2", label: "February" },
  { value: "3", label: "March" },   { value: "4", label: "April" },
  { value: "5", label: "May" },     { value: "6", label: "June" },
  { value: "7", label: "July" },    { value: "8", label: "August" },
  { value: "9", label: "September" },{ value: "10", label: "October" },
  { value: "11", label: "November" },{ value: "12", label: "December" },
];
const QUARTERS = [
  { value: "1", label: "Q1 (Jan – Mar)" },
  { value: "2", label: "Q2 (Apr – Jun)" },
  { value: "3", label: "Q3 (Jul – Sep)" },
  { value: "4", label: "Q4 (Oct – Dec)" },
];

const PRIORITY_COLORS = {
  critical: "#dc2626",
  high:     "#f97316",
  medium:   "#f59e0b",
  low:      "#22c55e",
};

const STATUS_COLORS = {
  new:           "#3b82f6",
  open:          "#3b82f6",
  "in progress": "#f59e0b",
  pending:       "#fb923c",
  "on hold":     "#a855f7",
  waiting:       "#8b5cf6",
  resolved:      "#22c55e",
  closed:        "#64748b",
  cancelled:     "#ef4444",
};

const FALLBACK_COLORS = ["#C04C2A", "#0ea5e9", "#14b8a6", "#e879f9", "#facc15"];

const colorForPriority = (label) => PRIORITY_COLORS[label.toLowerCase()] || "#C04C2A";
const colorForStatus = (label, i) => STATUS_COLORS[label.toLowerCase()] || FALLBACK_COLORS[i % FALLBACK_COLORS.length];

function SummaryCard({ icon, label, value, sub, color }) {
  return (
    <div className="rounded-2xl border bg-white p-4 shadow-sm" style={{ borderColor: "rgba(192,76,42,0.12)" }}>
      <div className="mb-2 flex items-center gap-2">
        <div className="flex h-8 w-8 items-center justify-center rounded-xl text-white text-sm" style={{ background: color || BRAND_135 }}>
          <FontAwesomeIcon icon={icon} />
        </div>
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</span>
      </div>
      <div className="truncate text-2xl font-extrabold text-slate-900">{value}</div>
      {sub && <div className="mt-0.5 text-[11px] text-slate-400">{sub}</div>}
    </div>
  );
}

function TopCustomersChart({ title, icon, data, barColor, emptyText }) {
  const chartData = data.map((c) => ({
    name: c.customerName,
    Count: c.count,
    Total: c.totalTickets,
  }));

  return (
    <div className="rounded-2xl border bg-white p-4 shadow-sm" style={{ borderColor: "rgba(192,76,42,0.12)" }}>
      <div className="mb-3 flex items-center gap-2">
        <FontAwesomeIcon icon={icon} style={{ color: barColor }} />
        <h2 className="text-sm font-bold text-slate-800">{title}</h2>
        <span className="ml-auto rounded-full border border-orange-200 bg-orange-50 px-2.5 py-0.5 text-[10px] font-bold" style={{ color: "#C04C2A" }}>
          Top {chartData.length}
        </span>
      </div>
      {chartData.length ? (
        <ResponsiveContainer width="100%" height={Math.max(260, chartData.length * 40 + 40)}>
          <BarChart data={chartData} layout="vertical" barSize={18} margin={{ top: 5, right: 48, left: 8, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 11, fill: "#64748b" }} allowDecimals={false} />
            <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 11, fill: "#334155", fontWeight: 600 }} interval={0} />
            <Tooltip
              formatter={(v, name) => [v, name === "Count" ? title.includes("Pending") ? "Pending" : "Resolved" : name]}
              contentStyle={{ fontSize: 12, borderRadius: 12, borderColor: "#fed7aa" }}
            />
            <Bar dataKey="Count" fill={barColor} radius={[0, 4, 4, 0]}>
              <LabelList dataKey="Count" position="right" style={{ fontSize: 11, fontWeight: 700, fill: barColor }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <p className="py-10 text-center text-sm text-slate-400">{emptyText}</p>
      )}
    </div>
  );
}

export default function CustomerTicketAnalyticsPage({ embedded = false }) {
  const [filters, setFilters] = useState(getDefaultCustomerTicketFilters);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [panelOpen, setPanelOpen] = useState(false);

  const runReport = useCallback(async (activeFilters) => {
    setLoading(true);
    setError("");
    try {
      const result = await fetchCustomerTicketAnalytics(activeFilters);
      setReport(result);
      return true;
    } catch (err) {
      const msg = err?.message || "Report could not be loaded.";
      setError(msg);
      showToast.error(msg);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    runReport(getDefaultCustomerTicketFilters());
  }, [runReport]);

  const handleRun = async () => {
    const ok = await runReport(filters);
    if (ok) setPanelOpen(false);
  };

  const handleReset = async () => {
    const next = getDefaultCustomerTicketFilters();
    setFilters(next);
    setError("");
    const ok = await runReport(next);
    if (ok) setPanelOpen(false);
  };

  const setFilter = (key, value) => setFilters((f) => ({ ...f, [key]: value }));

  // ── Derived data ──────────────────────────────────────────
  const summary  = report?.summary ?? {};
  const priority = report?.priorityBreakdown ?? [];
  const statuses = report?.statusBreakdown ?? [];
  const trend    = report?.trend ?? [];

  const priorityPie = priority.map((p) => ({ name: p.label, value: p.count, percent: p.percent }));
  const trendData = trend.map((t) => ({ name: t.label, Total: t.total, Pending: t.pending, Resolved: t.resolved }));

  const filterLabel =
    filters.mode === "monthly"
      ? `${MONTHS.find((m) => m.value === filters.month)?.label ?? ""} ${filters.year}`
      : filters.mode === "quarterly"
      ? `${QUARTERS.find((q) => q.value === filters.quarter)?.label ?? ""} ${filters.year}`
      : `Year ${filters.year}`;

  return (
    <div className={embedded ? "" : "min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 p-3 md:p-5"}>
      {!embedded && <Breadcrumb pageName="Customer Ticket Analytics" currentPage="Customer Ticket Analytics" />}

      {/* ── Hero Header ── */}
      <div className="mt-4 overflow-hidden rounded-3xl border border-orange-100 bg-white shadow-xl" style={{ boxShadow: "0 16px 48px rgba(192,76,42,0.10)" }}>
        <div className="relative overflow-hidden px-5 py-6 text-white md:px-7 md:py-8" style={{ background: BRAND_135 }}>
          <div className="pointer-events-none absolute -right-8 -top-8 h-40 w-40 rounded-full bg-white/5 blur-3xl" />
          <div className="pointer-events-none absolute bottom-0 left-1/4 h-24 w-24 rounded-full blur-2xl" style={{ background: "rgba(255,160,100,0.12)" }} />

          <div className="relative flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-orange-100">
                <FontAwesomeIcon icon={faTicket} />
                Customer Ticket Analytics
              </div>
              <h1 className="mt-2 text-lg font-extrabold tracking-tight text-white sm:text-xl">
                Pending Workload, Resolution &amp; Priority Insights
              </h1>
              <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
                <span className="rounded-full border border-white/15 bg-white/10 px-2.5 py-0.5 text-orange-100">{filterLabel}</span>
                <span className="rounded-full border border-white/15 bg-white/10 px-2.5 py-0.5 capitalize text-orange-100">{filters.mode} view</span>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setPanelOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-xl border border-white/20 bg-white/15 px-3 py-2 text-xs font-bold text-white transition hover:bg-white/25"
              >
                <FontAwesomeIcon icon={faFilter} /> Filters
              </button>
              <button
                type="button"
                onClick={handleRun}
                disabled={loading}
                className="inline-flex items-center gap-1.5 rounded-xl border border-white/20 bg-white/10 px-3 py-2 text-xs font-bold text-white transition hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? <DotLoader color="#fff" /> : <FontAwesomeIcon icon={faSearch} />}
                {loading ? "Loading…" : "Run Report"}
              </button>
            </div>
          </div>
        </div>

        <div className="space-y-6 p-4 md:p-6">
          {error && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-2.5 text-sm text-rose-700">{error}</div>
          )}

          {loading ? (
            <div className="rounded-2xl border border-dashed border-orange-200 bg-orange-50/30 px-6 py-14 text-center text-sm" style={{ color: "#C04C2A" }}>
              <DotLoader size="w-3 h-3" />
              <p className="mt-3 font-semibold">Loading customer ticket analytics...</p>
            </div>
          ) : report ? (
            <>
              {/* ── Summary Cards ── */}
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                <SummaryCard icon={faTicket}        label="Total Tickets"  value={summary.totalTickets}    sub={`${summary.totalCustomers} customers`} />
                <SummaryCard icon={faHourglassHalf} label="Pending"        value={summary.pendingTickets}  sub={summary.topPendingCustomer ? `Top: ${summary.topPendingCustomer}` : ""} color="linear-gradient(135deg,#fb923c,#ea580c)" />
                <SummaryCard icon={faCheckCircle}   label="Resolved"       value={summary.resolvedTickets} sub={`${summary.resolutionRate}% resolution rate`} color="linear-gradient(135deg,#22c55e,#16a34a)" />
                <SummaryCard icon={faTrophy}        label="Top Priority"   value={summary.topPriority || "—"} sub={`${summary.topPriorityCount} tickets`} color="linear-gradient(135deg,#dc2626,#991b1b)" />
                <SummaryCard icon={faUsers}         label="Top Resolver"   value={summary.topResolvedCustomer || "—"} sub={`${summary.topResolvedCustomerCount} resolved`} color="linear-gradient(135deg,#8b5cf6,#6d28d9)" />
              </div>

              {/* ── Top 10 Pending / Resolved ── */}
              <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                <TopCustomersChart
                  title="Top 10 Customers — Pending Tickets"
                  icon={faHourglassHalf}
                  data={report.topPendingCustomers}
                  barColor="#ea580c"
                  emptyText="No pending tickets in this period."
                />
                <TopCustomersChart
                  title="Top 10 Customers — Resolved Tickets"
                  icon={faCheckCircle}
                  data={report.topResolvedCustomers}
                  barColor="#16a34a"
                  emptyText="No resolved tickets in this period."
                />
              </div>

              {/* ── Priority + Status ── */}
              <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                {/* Priority Distribution */}
                <div className="rounded-2xl border bg-white p-4 shadow-sm" style={{ borderColor: "rgba(192,76,42,0.12)" }}>
                  <div className="mb-3 flex items-center gap-2">
                    <FontAwesomeIcon icon={faChartPie} style={{ color: "#C04C2A" }} />
                    <h2 className="text-sm font-bold text-slate-800">Ticket Priority Distribution</h2>
                  </div>
                  {priorityPie.length ? (
                    <div className="flex flex-col items-center gap-3 sm:flex-row">
                      <ResponsiveContainer width="100%" height={240}>
                        <PieChart>
                          <Pie data={priorityPie} cx="50%" cy="50%" innerRadius={55} outerRadius={95} dataKey="value" paddingAngle={2}>
                            {priorityPie.map((entry, i) => (
                              <Cell key={i} fill={colorForPriority(entry.name)} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(v, name, p) => [`${v} (${p?.payload?.percent}%)`, name]} contentStyle={{ fontSize: 12, borderRadius: 12, borderColor: "#fed7aa" }} />
                          <Legend wrapperStyle={{ fontSize: 11 }} />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="w-full space-y-1.5 sm:w-44">
                        {priority.map((p) => (
                          <div key={p.label} className="flex items-center justify-between rounded-lg border border-slate-100 px-2.5 py-1.5 text-xs">
                            <span className="flex items-center gap-1.5 font-semibold text-slate-600">
                              <span className="h-2.5 w-2.5 rounded-full" style={{ background: colorForPriority(p.label) }} />
                              {p.label}
                            </span>
                            <span className="font-bold text-slate-800">{p.count} <span className="text-[10px] font-semibold text-slate-400">({p.percent}%)</span></span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p className="py-10 text-center text-sm text-slate-400">No priority data in this period.</p>
                  )}
                </div>

                {/* Status Overview */}
                <div className="rounded-2xl border bg-white p-4 shadow-sm" style={{ borderColor: "rgba(192,76,42,0.12)" }}>
                  <div className="mb-3 flex items-center gap-2">
                    <FontAwesomeIcon icon={faTicket} style={{ color: "#C04C2A" }} />
                    <h2 className="text-sm font-bold text-slate-800">Ticket Status Overview</h2>
                  </div>
                  {statuses.length ? (
                    <>
                      <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-3">
                        {statuses.map((st, i) => (
                          <div key={st.label} className="rounded-xl border px-3 py-2" style={{ borderColor: `${colorForStatus(st.label, i)}33`, background: `${colorForStatus(st.label, i)}0d` }}>
                            <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: colorForStatus(st.label, i) }}>{st.label}</div>
                            <div className="text-lg font-extrabold text-slate-800">{st.count}</div>
                            <div className="text-[10px] font-semibold text-slate-400">{st.percent}%</div>
                          </div>
                        ))}
                      </div>
                      <ResponsiveContainer width="100%" height={160}>
                        <BarChart data={statuses.map((st) => ({ name: st.label, Count: st.count }))} barSize={28} margin={{ top: 16, right: 10, left: 0, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                          <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#64748b" }} interval={0} />
                          <YAxis tick={{ fontSize: 10, fill: "#64748b" }} allowDecimals={false} />
                          <Tooltip contentStyle={{ fontSize: 12, borderRadius: 12, borderColor: "#fed7aa" }} />
                          <Bar dataKey="Count" radius={[4, 4, 0, 0]}>
                            <LabelList dataKey="Count" position="top" style={{ fontSize: 10, fontWeight: 700, fill: "#475569" }} />
                            {statuses.map((st, i) => (
                              <Cell key={i} fill={colorForStatus(st.label, i)} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </>
                  ) : (
                    <p className="py-10 text-center text-sm text-slate-400">No status data in this period.</p>
                  )}
                </div>
              </div>

              {/* ── Trend ── */}
              <div className="rounded-2xl border bg-white p-4 shadow-sm" style={{ borderColor: "rgba(192,76,42,0.12)" }}>
                <div className="mb-3 flex items-center gap-2">
                  <FontAwesomeIcon icon={faChartLine} style={{ color: "#C04C2A" }} />
                  <h2 className="text-sm font-bold text-slate-800">Customer Ticket Trend</h2>
                </div>
                {trendData.length ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <LineChart data={trendData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} />
                      <YAxis tick={{ fontSize: 11, fill: "#64748b" }} allowDecimals={false} />
                      <Tooltip contentStyle={{ fontSize: 12, borderRadius: 12, borderColor: "#fed7aa" }} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Line type="monotone" dataKey="Total"    stroke="#C04C2A" strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="Pending"  stroke="#ea580c" strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="Resolved" stroke="#16a34a" strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="py-10 text-center text-sm text-slate-400">No trend data in this period.</p>
                )}
              </div>
            </>
          ) : (
            <div className="rounded-2xl border border-dashed px-6 py-14 text-center text-sm" style={{ borderColor: "rgba(192,76,42,0.22)", background: "rgba(192,76,42,0.02)", color: "#8B2A15" }}>
              Run the report to view customer ticket analytics.
            </div>
          )}
        </div>
      </div>

      {/* ── Filter Slide Panel ── */}
      <div className={`fixed inset-0 z-[80] ${panelOpen ? "" : "pointer-events-none"}`}>
        <button
          type="button"
          aria-label="Close filters"
          onClick={() => setPanelOpen(false)}
          className={`absolute inset-0 bg-slate-950/35 transition ${panelOpen ? "opacity-100" : "opacity-0"}`}
        />
        <div className={`absolute right-0 top-0 h-full w-full max-w-sm transform p-3 transition duration-300 sm:p-4 ${panelOpen ? "translate-x-0" : "translate-x-full"}`}>
          <div className="h-full overflow-y-auto rounded-[28px] bg-white shadow-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between rounded-t-[28px] px-5 py-4" style={{ background: BRAND_135 }}>
              <div className="flex items-center gap-2 text-sm font-bold text-white">
                <FontAwesomeIcon icon={faFilter} /> Report Filters
              </div>
              <button type="button" onClick={() => setPanelOpen(false)} className="flex h-7 w-7 items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/25">
                <FontAwesomeIcon icon={faXmark} className="text-xs" />
              </button>
            </div>

            <div className="space-y-4 p-5">
              {/* View Mode */}
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-slate-600">View</label>
                <div className="flex gap-2">
                  {["monthly", "quarterly", "yearly"].map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setFilter("mode", m)}
                      className="flex-1 rounded-lg border py-1.5 text-xs font-semibold capitalize transition"
                      style={{
                        background: filters.mode === m ? "#C04C2A" : "#fff",
                        color: filters.mode === m ? "#fff" : "#64748b",
                        borderColor: filters.mode === m ? "#C04C2A" : "#e2e8f0",
                      }}
                    >
                      {m}
                    </button>
                  ))}
                </div>
              </div>

              {/* Year */}
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-slate-600">Year</label>
                <select
                  value={filters.year}
                  onChange={(e) => setFilter("year", e.target.value)}
                  className="w-full rounded-xl border border-orange-100 bg-white px-3 py-2 text-sm text-slate-700 outline-none"
                  onFocus={(e) => { e.target.style.borderColor = "#C04C2A"; }}
                  onBlur={(e)  => { e.target.style.borderColor = ""; }}
                >
                  {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>

              {/* Month */}
              {filters.mode === "monthly" && (
                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-slate-600">Month</label>
                  <select
                    value={filters.month}
                    onChange={(e) => setFilter("month", e.target.value)}
                    className="w-full rounded-xl border border-orange-100 bg-white px-3 py-2 text-sm text-slate-700 outline-none"
                    onFocus={(e) => { e.target.style.borderColor = "#C04C2A"; }}
                    onBlur={(e)  => { e.target.style.borderColor = ""; }}
                  >
                    {MONTHS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                </div>
              )}

              {/* Quarter */}
              {filters.mode === "quarterly" && (
                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-slate-600">Quarter</label>
                  <select
                    value={filters.quarter}
                    onChange={(e) => setFilter("quarter", e.target.value)}
                    className="w-full rounded-xl border border-orange-100 bg-white px-3 py-2 text-sm text-slate-700 outline-none"
                    onFocus={(e) => { e.target.style.borderColor = "#C04C2A"; }}
                    onBlur={(e)  => { e.target.style.borderColor = ""; }}
                  >
                    {QUARTERS.map((q) => <option key={q.value} value={q.value}>{q.label}</option>)}
                  </select>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={handleReset}
                  className="flex-1 rounded-xl border border-slate-200 bg-white py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
                >
                  Reset
                </button>
                <button
                  type="button"
                  onClick={handleRun}
                  disabled={loading}
                  className="flex-1 rounded-xl py-2.5 text-sm font-bold text-white transition disabled:opacity-60"
                  style={{ background: BRAND_135 }}
                >
                  {loading ? <DotLoader color="#fff" /> : "Run Report"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
